import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import { isNewAifcExperienceEnabledNoExposure } from '../../ui/utils/new-aifc-experience-enabled';

import type { AIContentPositionEntry, SessionContextPluginState } from './types';

/**
 * CSS class applied to the inline decoration that shades an undone
 * Post Stream Review segment. Styled in `GlobalWrapper` (global editor styles)
 * with `token('color.background.neutral')`, mirroring how the other AI editor
 * decoration classes (e.g. `ai-selection-highlight`) are styled there.
 */
export const UNDONE_SEGMENT_DECORATION_CLASS = 'ai-undone-segment-highlight';

/**
 * CSS class / testid for the rework-range highlight painted over the re-prompt's
 * target range. Matches the class the decoration plugin previously used so styling
 * and selectors keep working.
 */
export const REWORK_RANGE_HIGHLIGHT_CLASS = 'ai-pre-stream-range-selection';

/**
 * Two ranges overlap when neither ends at or before the other starts. Used to
 * decide when the rework highlight supersedes an undone-segment gray for the
 * same region, so only one decoration ever paints there.
 */
const rangesOverlap = (
	a: { endPos: number; startPos: number },
	b: { endPos: number; startPos: number },
): boolean => a.startPos < b.endPos && b.startPos < a.endPos;

/**
 * Resolves the entries to shade, mirroring the PostStreamReview screen so the
 * decoration reads the same entries whose `appliedIndex` undo/redo mutates:
 *   - Gate ON + step-through: the flattened `diffSegments` leaves (skip accepted;
 *     fall back to the entry itself when it has no leaves).
 *   - Otherwise: the top-level `streamReviewSegments` (with `isUndoneSegment`
 *     collapsing leaf state for aggregate), falling back to the coarse
 *     `aiContentPositions` only when there are no fine segments yet.
 *
 * Using `aiContentPositions` in aggregate mode was the original bug: undo/redo
 * never writes `appliedIndex` there, so nothing was shaded.
 */
const getWorkingSet = (
	pluginState: SessionContextPluginState,
): AIContentPositionEntry[] | undefined => {
	const topLevel = pluginState.streamReviewSegments;
	// No fine segments yet (e.g. a transient pre-bootstrap state): the coarse
	// commit ranges are all we have.
	if (!topLevel || topLevel.length === 0) {
		return pluginState.aiContentPositions;
	}

	const isStepThrough = pluginState.currentStreamReviewMode === 'stepThrough';

	// Gate ON + step-through: shade the flattened diff-level leaves, matching the
	// screen's `displaySegments` flatten.
	if (isStepThrough && fg('platform_editor_ai_diff_based_segmenting')) {
		return topLevel
			.filter((entry) => !entry.isAccepted)
			.flatMap((entry) =>
				entry.diffSegments ? entry.diffSegments.filter((leaf) => !leaf.isAccepted) : [entry],
			);
	}

	// Gate OFF (either mode) or gate ON + aggregate: shade the top-level entries.
	return topLevel;
};

/**
 * A segment is "undone" when `appliedIndex === 0` (its original pre-AI content).
 * Accepted segments and zero-width ranges never qualify (nothing to shade, and
 * ProseMirror rejects an empty inline decoration).
 *
 * An aggregate parent carries undo state on its `diffSegments` leaves, so it is
 * "undone" only when every non-accepted leaf is itself undone.
 */
const isUndoneSegment = (entry: AIContentPositionEntry): boolean => {
	if (entry.isAccepted || entry.endPos <= entry.startPos) {
		return false;
	}
	if (entry.diffSegments && entry.diffSegments.length > 0) {
		const reviewableLeaves = entry.diffSegments.filter((leaf) => !leaf.isAccepted);
		return reviewableLeaves.length > 0 && reviewableLeaves.every((leaf) => leaf.appliedIndex === 0);
	}
	return entry.appliedIndex === 0;
};

/**
 * Builds the inline gray-background decoration set for every undone
 * segment in the current working set.
 *
 * Returns `DecorationSet.empty` unless the New AIFC Editor Experience is enabled
 * (no-exposure check — this runs in the render path) and the session is actively
 * in Post Stream Review, so the decoration is torn down for free when the review
 * closes (status leaves `postStreamReview`, e.g. on `RESET`).
 *
 * Positions are read straight from plugin state, which `apply()` already remaps
 * on every doc change, so the ranges are always current. Out-of-bounds ranges
 * are defended against `doc.content.size` so a transient mid-transaction state
 * can never throw.
 */
export const buildUndoneSegmentDecorationSet = (
	doc: PMNode,
	pluginState: SessionContextPluginState,
): DecorationSet => {
	if (!isNewAifcExperienceEnabledNoExposure()) {
		return DecorationSet.empty;
	}

	const docSize = doc.content.size;
	const decorations: Decoration[] = [];

	// The rework-range highlight. Not gated on `postStreamReview` — it must survive
	// the experience teardown that runs on re-prompt submit.
	const highlight = pluginState.reworkHighlightRange;
	let highlightRange: { endPos: number; startPos: number } | undefined;
	if (highlight) {
		const from = Math.max(0, Math.min(highlight.startPos, docSize));
		const to = Math.max(0, Math.min(highlight.endPos, docSize));
		if (from < to) {
			highlightRange = { startPos: from, endPos: to };
			decorations.push(
				Decoration.inline(
					from,
					to,
					{ class: REWORK_RANGE_HIGHLIGHT_CLASS, 'data-testid': REWORK_RANGE_HIGHLIGHT_CLASS },
					{ key: REWORK_RANGE_HIGHLIGHT_CLASS },
				),
			);
		}
	}

	// The undone-segment gray only paints while the Post Stream Review toolbar is
	// open, so the decoration is torn down for free when the review closes.
	if (pluginState.status === 'postStreamReview') {
		const workingSet = getWorkingSet(pluginState);
		workingSet?.forEach((entry) => {
			if (!isUndoneSegment(entry)) {
				return;
			}
			const from = entry.startPos;
			const to = entry.endPos;
			// Defend against a range that a transient state left outside the current
			// document — an inline decoration outside `[0, docSize]` throws.
			if (from < 0 || to > docSize || from >= to) {
				return;
			}
			// The highlight wins over the gray on the reworked range: skip any undone
			// segment overlapping the highlight so only one decoration renders there.
			if (highlightRange && rangesOverlap(highlightRange, { startPos: from, endPos: to })) {
				return;
			}
			decorations.push(
				Decoration.inline(
					from,
					to,
					{
						class: UNDONE_SEGMENT_DECORATION_CLASS,
						'data-testid': UNDONE_SEGMENT_DECORATION_CLASS,
					},
					// Key on the (non-overlapping) range, not the array index: accepting a
					// segment removes it from the front of the working set and would shift
					// every later index, making ProseMirror destroy/recreate all surviving
					// decorations. A position-based key keeps their identity stable.
					{ key: `${UNDONE_SEGMENT_DECORATION_CLASS}-${from}-${to}` },
				),
			);
		});
	}

	if (decorations.length === 0) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
};

/**
 * Creates a memoised decoration provider for the `ai-session-context`
 * plugin's `props.decorations`.
 *
 * `props.decorations(state)` is called by the view on EVERY `updateState` — not
 * just doc changes — so a naive rebuild would allocate a fresh `DecorationSet`
 * (and defeat ProseMirror's decoration diffing) on pure navigation transactions
 * that don't change the doc. We memoise on the raw inputs the decoration set
 * actually depends on:
 *   - `source`: the segment array the shading is derived from (`streamReviewSegments`
 *     when present, else the coarse `aiContentPositions` fallback).
 *   - `status` and `currentStreamReviewMode`: select which branch `getWorkingSet` takes.
 *   - `reworkHighlightRange`: set/cleared by navigation transactions that touch
 *     neither the doc nor the source array.
 *   - `doc`: guards against returning a set built against a stale document.
 *
 * Undo / redo / accept / doc-change all produce a new `source` array (or doc), so
 * the set is rebuilt exactly when — and only when — it needs to be. Pure toolbar
 * navigation (`UPDATE_STREAM_REVIEW_SEGMENT`, which only changes the current
 * segment index — not read here) leaves every key unchanged, so the cached set is
 * returned untouched. `getWorkingSet` runs only on a cache miss, so its per-call
 * `diffSegments` flatten no longer needs separate reference-stabilisation.
 *
 * The memo is per-plugin-instance (closure), so it never leaks across editors.
 */
export const createUndoneSegmentDecorationProvider = (): ((
	doc: PMNode,
	pluginState: SessionContextPluginState,
) => DecorationSet) => {
	let cachedSource: AIContentPositionEntry[] | undefined;
	let cachedStatus: SessionContextPluginState['status'];
	let cachedMode: SessionContextPluginState['currentStreamReviewMode'];
	let cachedHighlightRange: SessionContextPluginState['reworkHighlightRange'];
	let cachedDoc: PMNode | undefined;
	let cachedSet: DecorationSet = DecorationSet.empty;

	return (doc: PMNode, pluginState: SessionContextPluginState): DecorationSet => {
		const hasFineSegments =
			!!pluginState.streamReviewSegments && pluginState.streamReviewSegments.length > 0;
		const source = hasFineSegments
			? pluginState.streamReviewSegments
			: pluginState.aiContentPositions;

		if (
			source === cachedSource &&
			pluginState.status === cachedStatus &&
			pluginState.currentStreamReviewMode === cachedMode &&
			pluginState.reworkHighlightRange === cachedHighlightRange &&
			doc === cachedDoc
		) {
			return cachedSet;
		}

		cachedSet = buildUndoneSegmentDecorationSet(doc, pluginState);
		cachedSource = source;
		cachedStatus = pluginState.status;
		cachedMode = pluginState.currentStreamReviewMode;
		cachedHighlightRange = pluginState.reworkHighlightRange;
		cachedDoc = doc;
		return cachedSet;
	};
};
